import React, { useEffect, useMemo, useState } from 'react';
import './MarketplaceCommsHub.scss';
import { useCurrentMarketplaceParty } from '../../../../custom-hooks/useCurrentMarketplaceParty';
import {
    getMarketplaceCommsHubTabs,
    MarketplaceCommsHubTab,
} from './MarketplaceCommsHubTabs';
import { Timeline } from '../../../../slices/marketplace/timeline/models/timeline.model';
import { MarketplaceBuyerCommunicationsTab } from './Tabs/MarketplaceBuyerCommunicationsTab/MarketplaceBuyerCommunicationsTab';
import { MarketplaceBrokerChatTab } from './Tabs/MarketplaceBrokerChatTab/MarketplaceBrokerChatTab';
import { NotificationFlag } from '../../../../components/NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../../../models/NotificationFlags/NotificationFlagsCategories';
import { TimelineParty } from '../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { MarketplaceOpportunity } from '../../../../slices/marketplace/opportunity/models/opportunity';
import { useSearchParams } from 'react-router-dom';
import NoPageAccessAlert from '../../../../components/NoPageAccessAlert/NoPageAccessAlert';
import EngagementMeetings from '../../../../containers/ConnectMarketplaceDashboard/EngagementMeetings/EngagementMeetings';
import { useHasMeetingsOrClips } from '../../../../custom-hooks/useHasMeetingsOrClips';
import { useFeatureFlag } from '../../../../custom-hooks/useFeatureFlag';
import { FeatureFlagsEnum } from '../../../FeatureFlags/FeatureFlags.enum';
import { EngagementMeetingsScheduling } from '../../../../containers/ConnectMarketplaceDashboard/EngagementMeetingsScheduling/EngagementMeetingsScheduling';
import * as dates from 'date-arithmetic';
import { TimelineActionType } from '../../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { useStateSelector } from '../../../../store/selectors';
import { useDispatch } from 'react-redux';
import { EngagementMeetingsPhase } from '../../../../slices/marketplace/meetings/models/enums/engagement-meetings-phase.enum';
import { TimelineEventStatus } from '../../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import { FetchEngagementAvailabilityBlocks } from '../../../../slices/marketplace/meetings/thunks/fetch-engagement-availability-blocks.thunk';
import { FetchEngagementMeetingProposals } from '../../../../slices/marketplace/meetings/thunks/fetch-engagement-meeting-proposals.thunk';
import { FetchEngagementConferences } from '../../../../slices/marketplace/meetings/thunks/fetch-engagement-conferences.thunk';
import { MeetingParticipantsType } from '../../../../slices/marketplace/meetings/models/enums/meeting-participant-type.enum';
import { RefreshNotificationFlags } from '../../../../slices/notification-flags/refresh-notification-flags';

interface MarketplaceCommsHubProps {
    engagement: Timeline;
    engagementId: number;
    meetingsGroupId?: number;
    setSearchParams?: (params: { [key: string]: string }) => void;
    notificationFlagPrefix: string;
    isReadOnly?: boolean;
}

export const MarketplaceCommsHub = (props: MarketplaceCommsHubProps) => {
    const dispatch = useDispatch();

    const marketplaceParty = useCurrentMarketplaceParty(props.engagement.id);
    const hasMeetingsOrClips = useHasMeetingsOrClips(props.engagementId);
    const meetingsState = useStateSelector((state) => state.engagementMeetings);
    const isFF_MrktplcSellers_2_13Enabled = useFeatureFlag(
        FeatureFlagsEnum.FF_MrktplcSellers_2_13
    );

    const [searchParams] = useSearchParams({});
    const [activeTab, setActiveTab] = useState<MarketplaceCommsHubTab>();

    const [meetingPeriodStartDate, setMeetingPeriodStartDate] = useState<
        Date | undefined
    >();
    const [meetingPeriodEndDate, setMeetingPeriodEndDate] = useState<
        Date | undefined
    >();

    const schedulingMode = useMemo(():
        | 'opportunity'
        | 'engagement'
        | 'buyerTopLevel'
        | 'sellerTopLevel' => {
        return marketplaceParty === TimelineParty.Buyer
            ? 'opportunity'
            : 'engagement';
    }, [marketplaceParty]);

    const engagementSellerBuyerMeetings = useMemo(() => {
        return meetingsState.engagementMeetings.filter(
            (meeting) =>
                meeting.engagementId === props.engagementId &&
                meeting.participantsType ===
                    MeetingParticipantsType.SellerBuyer
        );
    }, [
        meetingsState.engagementMeetings,
        meetingsState.status,
        props.engagementId,
    ]);
    const meetingProposals = useMemo(() => {
        return meetingsState.meetingProposals.filter(
            (proposal) => proposal.engagementId === props.engagementId
        );
    }, [
        meetingsState.meetingProposals,
        meetingsState.status,
        props.engagementId,
    ]);
    const availabilityBlocks = useMemo(() => {
        return meetingsState.availabilityBlocks;
    }, [
        meetingsState.availabilityBlocks,
        meetingsState.status,
        props.engagementId,
    ]);

    const meetingsPhase = useMemo(() => {
        if (!!props.engagement) {
            const timelineEvents = props.engagement?.events || [];

            const availabilityEvent = timelineEvents.find(
                (event) =>
                    event.linkedAction?.actionType ===
                    TimelineActionType.SellerProvideMeetingAvailability
            );
            if (
                !!availabilityEvent &&
                availabilityEvent.status === TimelineEventStatus.InProgress
            ) {
                return EngagementMeetingsPhase.ProvideAvailability;
            }

            const agreeOnTimesEvent = timelineEvents.find(
                (event) =>
                    event.linkedAction?.actionType ===
                    TimelineActionType.SellerBuyerAgreeOnMeetingTimes
            );
            if (
                !!agreeOnTimesEvent &&
                agreeOnTimesEvent.status === TimelineEventStatus.InProgress
            ) {
                return EngagementMeetingsPhase.AgreeOnTimes;
            }

            const meetingsPeriodEvent = timelineEvents.find(
                (event) =>
                    event.linkedAction?.actionType ===
                    TimelineActionType.SellerBuyerMeetingsPeriod
            );
            if (
                !!meetingsPeriodEvent &&
                meetingsPeriodEvent.status === TimelineEventStatus.InProgress
            ) {
                return EngagementMeetingsPhase.MeetingsPeriod;
            } else if (
                !!meetingsPeriodEvent &&
                meetingsPeriodEvent.status === TimelineEventStatus.Completed
            ) {
                return EngagementMeetingsPhase.Completed;
            }
        }
        return EngagementMeetingsPhase.NotStarted;
    }, [props.engagement, props.engagementId]);

    const canFetchAvailabilityBlocks =
        meetingsPhase === EngagementMeetingsPhase.ProvideAvailability ||
        meetingsPhase === EngagementMeetingsPhase.AgreeOnTimes ||
        meetingsPhase === EngagementMeetingsPhase.MeetingsPeriod;

    const refreshMeetings = () => {
        if (
            meetingsPhase > EngagementMeetingsPhase.NotStarted &&
            meetingsPhase < EngagementMeetingsPhase.Completed
        ) {
            if (canFetchAvailabilityBlocks) {
                dispatch(
                    FetchEngagementAvailabilityBlocks(props.engagementId) as any
                );
            }
            if (meetingsPhase > EngagementMeetingsPhase.ProvideAvailability) {
                dispatch(
                    FetchEngagementMeetingProposals(props.engagementId) as any
                );
                dispatch(
                    FetchEngagementConferences(props.engagementId) as any
                );
            }
        }
    };

    useEffect(() => {
        if (props.engagementId && isFF_MrktplcSellers_2_13Enabled) {
            refreshMeetings();
        }
    }, [schedulingMode, props.engagementId, dispatch, meetingsPhase]);

    const getTimelineId = () => {
        if (marketplaceParty === TimelineParty.Buyer) {
            const selectedOpportunity =
                props.engagement as unknown as MarketplaceOpportunity;
            return selectedOpportunity.opportunityId;
        } else {
            return props.engagement.id;
        }
    };

    useEffect(() => {
        const meetingPeriodEvent = props.engagement.events.find(
            (event) =>
                event.linkedAction?.actionType ===
                TimelineActionType.SellerBuyerMeetingsPeriod
        );
        if (!!meetingPeriodEvent) {
            const engagementStartDate =
                schedulingMode === 'engagement'
                    ? props.engagement.engagementProcess?.startDate
                    : (props.engagement as unknown as MarketplaceOpportunity)
                          .listingDate;
            const startDate = dates.add(
                engagementStartDate,
                meetingPeriodEvent.dayStart,
                'day'
            );
            const endDate = dates.add(
                engagementStartDate,
                meetingPeriodEvent.dayEnd - 1,
                'day'
            );

            setMeetingPeriodStartDate(startDate);
            setMeetingPeriodEndDate(endDate);
        } else {
            const meetingTimesEvent = props.engagement.events.find(
                (event) =>
                    event.linkedAction?.actionType ===
                    TimelineActionType.SellerBuyerAgreeOnMeetingTimes
            );
            const availabilityEvent = props.engagement.events.find(
                (event) =>
                    event.linkedAction?.actionType ===
                    TimelineActionType.SellerProvideMeetingAvailability
            );
            if (!!meetingTimesEvent) {
                const startDate = dates.add(
                    props.engagement.engagementProcess?.startDate,
                    meetingTimesEvent.dayStart + 1,
                    'day'
                );
                setMeetingPeriodStartDate(startDate);
            } else if (!!availabilityEvent) {
                const startDate = dates.add(
                    props.engagement.engagementProcess?.startDate,
                    availabilityEvent.dayStart + 1,
                    'day'
                );
                setMeetingPeriodStartDate(startDate);
            } else {
                setMeetingPeriodStartDate(dates.add(new Date(), 1, 'day'));
            }

            setMeetingPeriodEndDate(
                props.engagement.engagementProcess?.deadline
            );
        }
    }, [props.engagement]);

    useEffect(() => {
        const isAdminOrAdvisor =
            marketplaceParty === TimelineParty.VetValue ||
            marketplaceParty === TimelineParty.Broker ||
            marketplaceParty === TimelineParty.Observer;

        let isCurrentBuyerActiveAndHasNda = false;
        if (marketplaceParty === TimelineParty.Buyer) {
            const selectedOpportunity =
                props.engagement as unknown as MarketplaceOpportunity;
            isCurrentBuyerActiveAndHasNda = !!(
                selectedOpportunity?.buyer?.isActive &&
                selectedOpportunity?.isDataNdaSigned &&
                selectedOpportunity?.isBuyerChatAvailable
            );
        }

        const tabs = getMarketplaceCommsHubTabs(
            marketplaceParty,
            props.engagement.levelOfService,
            props.engagement.engagementProcess?.buyers?.some(
                (b) => b.isDataNdaSigned && b.isActive
            ),
            doesBrokerChatExist(),
            props.engagement.events ?? [],
            hasMeetingsOrClips,
            isAdminOrAdvisor,
            isCurrentBuyerActiveAndHasNda,
            isFF_MrktplcSellers_2_13Enabled
        );

        const value = searchParams.get('subtab');
        const numberValue = Number(value);

        if (
            value &&
            numberValue &&
            numberValue >= 1 &&
            numberValue <= tabComponents.length
        ) {
            const isTabAvailable = tabs.some((tab) => tab.id === numberValue);
            if (isTabAvailable) {
                setActiveTab(numberValue);
            } else if (tabs.length > 0) {
                setActiveTab(tabs[0]?.id);
            } else {
                setActiveTab(undefined);
            }
        } else {
            if (tabs.length > 0) {
                setActiveTab(tabs[0]?.id);
            } else {
                setActiveTab(undefined);
            }
        }

        if (numberValue > tabComponents.length) {
            props.setSearchParams?.({
                subtab: '',
            });
        }
    }, [searchParams, hasMeetingsOrClips]);

    useEffect(() => {
        if (activeTab != null) {
            (dispatch as any)(RefreshNotificationFlags());
        }
    }, [activeTab, dispatch]);

    const doesBrokerChatExist = () => {
        const broker = props.engagement?.engagementProcess?.broker;
        return !!(broker && broker.isActive && broker.brokerChatId);
    };

    const tabComponents = [
        {
            id: MarketplaceCommsHubTab.BrokerChat,
            content: (
                <MarketplaceBrokerChatTab
                    party={marketplaceParty}
                    timeline={props.engagement}
                    isReadOnly={props.isReadOnly}
                />
            ),
            notificationFlag: `${props.notificationFlagPrefix}.${props.engagement?.id}.${NotificationFlagsCategories.CommsHubCode}.${NotificationFlagsCategories.BrokerChatCode}`,
        },
        {
            id: MarketplaceCommsHubTab.BuyerCommunications,
            content: (
                <MarketplaceBuyerCommunicationsTab
                    party={marketplaceParty}
                    timeline={props.engagement}
                    notificationFlagPrefix={props.notificationFlagPrefix}
                    isReadOnly={props.isReadOnly}
                />
            ),
            notificationFlag: `${
                props.notificationFlagPrefix
            }.${getTimelineId()}.${NotificationFlagsCategories.CommsHubCode}.${
                NotificationFlagsCategories.BuyerCommsCode
            }`,
        },
        {
            id: MarketplaceCommsHubTab.Meetings,
            content: (
                <EngagementMeetings
                    engagementId={props.engagementId}
                    meetingsGroupId={props.meetingsGroupId}
                    engagementBuyers={
                        props.engagement.engagementProcess?.buyers
                    }
                    isBrokerExist={
                        props.engagement.engagementProcess?.broker?.isActive
                    }
                    events={props.engagement.events}
                    engagementProcess={props.engagement.engagementProcess}
                    notificationFlagPrefix={props.notificationFlagPrefix}
                    isReadOnly={props.isReadOnly}
                />
            ),
            notificationFlag: `${props.notificationFlagPrefix}.${props.engagementId}.${NotificationFlagsCategories.CommsHubCode}.${NotificationFlagsCategories.MeetingCode}`,
        },
        {
            id: MarketplaceCommsHubTab.MeetingScheduling,
            content: (
                <EngagementMeetingsScheduling
                    mode={schedulingMode}
                    engagementId={props.engagementId}
                    startDate={meetingPeriodStartDate}
                    endDate={meetingPeriodEndDate}
                    party={marketplaceParty}
                    buyers={props.engagement.engagementProcess?.buyers}
                    onRefreshMeetings={refreshMeetings}
                    meetings={engagementSellerBuyerMeetings}
                    meetingProposals={meetingProposals}
                    availabilityBlocks={
                        canFetchAvailabilityBlocks ? availabilityBlocks : []
                    }
                    isReadOnly={props.isReadOnly}
                />
            ),
            notificationFlag: `${props.notificationFlagPrefix}.${props.engagementId}.${NotificationFlagsCategories.CommsHubCode}.${NotificationFlagsCategories.MeetingProposalCode}`,
        },
    ];

    const renderTabContent = () => {
        if (!activeTab) {
            return null;
        }
        const activeTabComponent = tabComponents.find(
            (tab) => tab.id === activeTab
        );
        const isBuyerChatsEnabled =
            props.engagement.engagementProcess?.isBuyerChatsEnabled ||
            (props.engagement as unknown as MarketplaceOpportunity)
                .isBuyerChatsEnabled;
        return activeTabComponent?.id ===
            MarketplaceCommsHubTab.BuyerCommunications &&
            !isBuyerChatsEnabled &&
            marketplaceParty !== TimelineParty.VetValue ? (
            <div className="mt-4">
                <NoPageAccessAlert text={'Chat is deactivated'} />
            </div>
        ) : (
            activeTabComponent?.content || (
                <div className="d-flex align-items-center justify-content-center mt-4">
                    <h1>Empty Tab</h1>
                </div>
            )
        );
    };

    const getTabNotificationFlag = (tab: number) => {
        const targetCode = tabComponents.find((t) => t.id === tab);
        return targetCode ? (
            <>
                <NotificationFlag
                    flagClassName="notification-flag-wrapper"
                    eventCode={targetCode.notificationFlag}
                    includePrefix={true}
                />
            </>
        ) : (
            <></>
        );
    };

    return (
        <div className="marketplace-comms-hub marketplace-dynamic-container">
            <div className="RRT__container">
                <div className="RRT__tabs body-tabs">
                    {(() => {
                        const isAdminOrAdvisor =
                            marketplaceParty === TimelineParty.VetValue ||
                            marketplaceParty === TimelineParty.Broker ||
                            marketplaceParty === TimelineParty.Observer;

                        let isCurrentBuyerActiveAndHasNda = false;
                        if (marketplaceParty === TimelineParty.Buyer) {
                            const selectedOpportunity =
                                props.engagement as unknown as MarketplaceOpportunity;
                            isCurrentBuyerActiveAndHasNda = !!(
                                selectedOpportunity?.buyer?.isActive &&
                                selectedOpportunity?.isDataNdaSigned &&
                                selectedOpportunity?.isBuyerChatAvailable
                            );
                        }

                        return getMarketplaceCommsHubTabs(
                            marketplaceParty,
                            props.engagement.levelOfService,
                            props.engagement.engagementProcess?.buyers?.some(
                                (b) => b.isDataNdaSigned && b.isActive
                            ) ?? false,
                            !!props.engagement?.engagementProcess?.broker
                                ?.brokerChatId &&
                                props.engagement.engagementProcess.broker
                                    .isActive,
                            props.engagement.events ?? [],
                            hasMeetingsOrClips,
                            isAdminOrAdvisor,
                            isCurrentBuyerActiveAndHasNda,
                            isFF_MrktplcSellers_2_13Enabled
                        );
                    })()
                        .filter(
                            (tab) =>
                                !(
                                    tab.id ===
                                        MarketplaceCommsHubTab.BuyerCommunications &&
                                    !(
                                        props.engagement.engagementProcess
                                            ?.isBuyerChatsEnabled ||
                                        (
                                            props.engagement as unknown as MarketplaceOpportunity
                                        ).isBuyerChatsEnabled
                                    ) &&
                                    marketplaceParty !== TimelineParty.VetValue
                                )
                        )
                        .map((item) => {
                            return (
                                <div
                                    key={item.id}
                                    className={
                                        activeTab === item.id
                                            ? 'RRT__tab RRT__tab--first RRT__tab--selected'
                                            : 'RRT__tab'
                                    }
                                    onClick={() => {
                                        setActiveTab(item.id);
                                        props.setSearchParams?.({
                                            subtab: item.id.toString(),
                                        });
                                    }}>
                                    {item.content}
                                    {getTabNotificationFlag(item.id)}
                                </div>
                            );
                        })}
                </div>
            </div>
            {renderTabContent()}
        </div>
    );
};
